import moment from 'moment'

import { DateError, DateFormat, DateFormatOutput, DateTimeFormat } from '@/interfaces/Date.interfaces'
import { LOCALE_DATE_FORMAT } from '@/constants/Language.constants'

/**
 * Takes an isoString and returns only the date part formatted as such : yyyy-mm-dd
 */
export const getDateFromISOString = (isoString: string): string => {
  const date = new Date(isoString)
  return moment(date).format(DateFormatOutput.ISO)
}

export const datetime = (value?: string): string | void => {
  if (value) {
    return moment(String(value)).format(DateFormatOutput.LLL)
  }
}

export const date = (value?: string): string | void => {
  if (value) {
    return moment(value, DateFormat.FR).format(DateFormatOutput.L)
  }
}

export const dateLarge = (value?: string): string | void => {
  if (value) {
    return moment(value, DateFormat.FR).format(DateFormatOutput.LL)
  }
}

export const datetimeSmall = (value?: string): string => {
  if (value) {
    return moment(value, DateTimeFormat.FR).local().format(DateFormatOutput.L_LT)
  }
  return DateError.CARET
}

export const localDatetime = (value?: string): string => {
  if (value) {
    return moment.utc(value, DateTimeFormat.FR).local().format(DateFormatOutput.L_LT)
  }
  return DateError.CARET
}

export const localDatetimeDate = (value?: string): string => {
  if (value) {
    return moment.utc(value, DateTimeFormat.FR).local().format(DateFormatOutput.L)
  }
  return DateError.CARET
}

export const localDatetimeLarge = (value?: string): string => {
  if (value) {
    return moment.utc(value, DateTimeFormat.FR).local().format(DateFormatOutput.LLL)
  }
  return DateError.CARET
}

export const dateFormat = (value?: string): string => {
  return moment.utc(value).local().format(DateFormatOutput.L_LT)
}

export const datetimeFormat = localDatetime

export const datetimeFormatUtc = (value?: string): string => {
  if (value) {
    return moment.utc(value, DateTimeFormat.FR).format(DateFormatOutput.L_LT)
  }
  return DateError.CARET
}

export const datetimeFormatNoHour = (value?: string): string => {
  if (value) {
    return moment.utc(value, DateFormat.FR).local().format(DateFormatOutput.L)
  }
  return DateError.CARET
}

/**
 * Convert an ISO date string : YYYY-MM-DD to a date with corresponding user lang format
 */
export const formatIsoDate = (isoDate: string, userLang: string): string => {
  if (!isoDate) {
    return DateError.CARET
  }

  const parsedIsoDate = moment(isoDate)

  if (!parsedIsoDate.isValid()) {
    return DateError.CARET
  }

  return parsedIsoDate.format(LOCALE_DATE_FORMAT[userLang])
}

/**
 * Parses a date string with the given moment format and returns its epoch (ms).
 * Returns `null` for empty input or invalid dates — suitable for null-aware comparators.
 */
export const parseTime = (value: string | null, format: string): number | null => {
  if (!value) {
    return null
  }
  const parsed = moment(value, format)
  return parsed.isValid() ? parsed.valueOf() : null
}

/**
 * Returns the week number of the year for a given date (weeks start on Sunday).
 */
export const getWeekNumber = (date: Date): number => {
  const startDate = new Date(date.getFullYear(), 0, 1)
  const dayNumber = Math.floor((date.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000))
  return Math.ceil((dayNumber + startDate.getDay() + 1) / 7)
}
